<template>
  <SimpleModal
    title="common.import"
    okText="g.25100001"
    size="middle"
    destroyOnClose
    :okButtonProps="{ disabled: hasError }"
    :confirmLoading="importing"
    :closeFunc="async () => !importing && !loading"
    :afterClose="resetData"
    @register="registerModal"
    @ok="doImport"
  >
    <div class="p-16px pt-6px flex items-center">
      <MUpload uploadType="excel" @upload-success="uploadSuccess" />
      <SyncExportBtn
        v-if="props.template && Object.keys(props.template).length"
        type="link"
        :path="props.template.path || '/low-code/lowCode/universal/file/template/download'"
        :params="getTemplateParams"
      >
        {{ t('c.25090337') }}
      </SyncExportBtn>
      <span class="ml-2 text-[#bdbdbd]">
        {{ t('c.25090336') + ACCEPT_EXTS.split(',').join(' ') }}</span
      >
    </div>
    <MTabs
      v-if="isMoreSheet && importData.titleList.length"
      v-model:activeKey="tabKey"
      :options="tabOptions"
      class="!px-16px"
      @change="onTabChange"
    />
    <BasicTable
      v-if="importData.titleList.length || loading"
      class="import-excel-table !h-[calc(100%-60px)]"
      @register="registerTable"
    />
    <template #insertFooter>
      <div v-if="importData.infoList.length" class="leading-32px absolute">
        {{ t('c.25090335') }}：{{ importData.infoList.length }} {{ t('c.25090332') }}
        <span v-if="importData.importErrorList.length" class="text-[#f66362]">
          {{ t('c.25090338', { count: importData.importErrorList.length }) }}
        </span>
        <span v-if="importData.importWarningList.length" class="text-[#ff752b]">
          {{ t('c.25100282', { count: importData.importWarningList.length }) }}
        </span>
      </div>
    </template>
  </SimpleModal>
</template>

<script setup lang="ts">
  import { Tooltip } from 'ant-design-vue';
  import { Key } from 'ant-design-vue/es/_util/type';
  import { h, nextTick, reactive, ref } from 'vue';
  import { Option } from '@mts-fe/types';
  import { getTableColumns, isArray, message } from '@mts-fe/utils';
  import { MTabs } from '@mts/components/Base';
  import { SyncExportBtn } from '@mts/components/Export';
  import { SimpleModal, useModalInner } from '@mts/components/Modal';
  import { BasicColumn, BasicTable, formatCell, useTable } from '@mts/components/Table';
  import { t } from '@mts/hooks/web/useI18n';
  import { createApi } from '@mts/render/api';
  import { MUpload } from '@mts/render/components/Upload';
  import type { IMP_MSG, ImportProps } from '..';

  const ACCEPT_EXTS = '.xls,.xlsx';
  const props = withDefaults(defineProps<ImportProps>(), {
    template: () => ({}),
  });
  const getTemplateParams = () => {
    const { params, ...rest } = props.template;
    const _params = typeof params === 'function' ? params() : params;
    return { ...rest, ..._params };
  };

  const [registerModal, modalAction] = useModalInner();
  const resetData = async () => {
    importData.titleList = [];
    importData.infoList = [];
    importData.importErrorList = [];
    importData.importWarningList = [];
    hasError.value = true;
  };

  const rowKey = '行号';
  const [registerTable, tableAction] = useTable({ isMounted: true, pagination: false, rowKey });

  const importing = ref(false);
  const loading = ref(false);
  const hasError = ref(true);
  const importData = reactive({
    titleList: [] as string[],
    infoList: [] as Recordable<string>[],
    importErrorList: [] as IMP_MSG[],
    importWarningList: [] as IMP_MSG[],
  });

  const getParams = () => (typeof props.params == 'function' ? props.params() : props.params) || {};
  const getErrorInfo = (title: string, rowIndex: string) =>
    importData.importErrorList.find((i) => i.title === title && i.index.toString() === rowIndex);
  const getWarnInfo = (title: string, rowIndex: string) =>
    importData.importWarningList.find((i) => i.title === title && i.index.toString() === rowIndex);

  const renderTableData = (res: Recordable) => {
    tabKey.value = res.templateKey;
    importData.titleList = res.titleList || [];
    importData.infoList = res.infoList || [];
    importData.importErrorList = res.importErrorList || [];
    importData.importWarningList = res.importWarningList || [];
    const columns = importData.titleList.map(
      (title, index) =>
        ({
          title: title == rowKey ? t('g.25100002') : title,
          dataIndex: title,
          width: index === 0 ? 45 : 100,
          fixed: index === 0 ? 'left' : undefined,
          customCell: (record) => {
            const className: string[] = [];
            const curError = getErrorInfo(title, record[rowKey]);
            const curWarn = getWarnInfo(title, record[rowKey]);
            curError && className.push('table-cell-error');
            curWarn && className.push('table-cell-warn');
            (curError || curWarn) && className.push('cursor-pointer');
            return { class: className.join(' ') };
          },
          customRender: ({ text, record, index }) => {
            const content = formatCell(text, 'split|\n', record, index);
            const curError = getErrorInfo(title, record[rowKey]);
            const curWarn = getWarnInfo(title, record[rowKey]);
            if (!curError && !curWarn) return content;

            return h(
              Tooltip,
              {
                title: curError?.msg || curWarn?.msg,
                color: curError ? '#f66362' : curWarn ? '#ff752b' : '',
                class: 'block',
              },
              () => content || '-',
            );
          },
        }) as BasicColumn,
    );
    tableAction.setProps({ columns: getTableColumns(columns), dataSource: importData.infoList });
  };

  const tabKey = ref<string>();
  const tabOptions = ref<Option[]>([]);
  const isMoreSheet = ref(false);
  const uploadFileUrl = ref('');
  const onTabChange = (key: Key) =>
    renderTableData(tabOptions.value.find((item) => item.value === key)!);

  function uploadSuccess({ fileUrl }) {
    resetData();
    uploadFileUrl.value = fileUrl;
    if (!props.action) return console.warn('请配置导入接口', props);

    loading.value = true;
    nextTick(() =>
      tableAction.setProps({
        loading: true,
        columns: [{ title: t('g.25100002'), dataIndex: rowKey }],
      }),
    );
    createApi(props.action)({
      ...getParams(),
      templateKey: props.templateKey,
      fileUrl,
      realUpload: false,
    })
      .then((res) => {
        isMoreSheet.value = isArray(res);
        if (isArray(res)) {
          tabOptions.value = res.map((i) => ({ ...i, label: i.templateKey, value: i.templateKey }));
          hasError.value = tabOptions.value.some((i) => !!i.importErrorList?.length);
          renderTableData(tabOptions.value[0]);
        } else {
          hasError.value = res.infoList?.length ? !!res.importErrorList?.length : true;
          renderTableData(res);
        }
      })
      .finally(() => ((loading.value = false), tableAction.setLoading(false)));
  }

  const emit = defineEmits(['register', 'ok']);
  async function doImport() {
    if (importing.value || !props.action) return;

    importing.value = true;
    createApi(props.action)({
      ...getParams(),
      realUpload: true,
      templateKey: props.templateKey,
      ...(isMoreSheet.value
        ? { fileUrl: uploadFileUrl.value }
        : { infoList: importData.infoList, titleList: importData.titleList }),
    })
      .then((res) => {
        message.success(t('c.25090229'));
        modalAction.closeModal();
        emit('ok', res);
      })
      .finally(() => (importing.value = false));
  }
</script>
